import apiClient from '../client';
import type { PaymentRequest, CreatePaymentRequestRequest, CreateProcurementRequestRequest, GeneralPaymentRequestRequest, PaginatedResponse, PaymentRequestStatus } from '../../types';

const paymentRequestService = {
  getAll: async (filters: { page?: number; size?: number } = {}) => {
    const { page = 0, size = 20 } = filters;
    const params = new URLSearchParams({ page: page.toString(), size: size.toString() });
    const response = await apiClient.get<PaginatedResponse<PaymentRequest>>(`/payment-requests?${params}`);
    return response.data;
  },

  getMyRequests: async (filters: { page?: number; size?: number } = {}) => {
    const { page = 0, size = 20 } = filters;
    const params = new URLSearchParams({ page: page.toString(), size: size.toString() });
    const response = await apiClient.get<PaginatedResponse<PaymentRequest>>(`/payment-requests/my?${params}`);
    return response.data;
  },

  getById: async (id: string) => {
    const response = await apiClient.get<PaymentRequest>(`/payment-requests/${id}`);
    return response.data;
  },

  getByProject: async (projectId: string, filters: { page?: number; size?: number } = {}) => {
    const { page = 0, size = 10 } = filters;
    const params = new URLSearchParams({ page: page.toString(), size: size.toString() });
    const response = await apiClient.get<PaginatedResponse<PaymentRequest>>(`/payment-requests/project/${projectId}?${params}`);
    return response.data;
  },

  getByFarmOperation: async (farmOperationId: string, filters: { page?: number; size?: number } = {}) => {
    const { page = 0, size = 10 } = filters;
    const params = new URLSearchParams({ page: page.toString(), size: size.toString() });
    const response = await apiClient.get<PaginatedResponse<PaymentRequest>>(`/payment-requests/operation/${farmOperationId}?${params}`);
    return response.data;
  },

  getByOperationTask: async (operationTaskId: string, filters: { page?: number; size?: number } = {}) => {
    const { page = 0, size = 10 } = filters;
    const params = new URLSearchParams({ page: page.toString(), size: size.toString() });
    const response = await apiClient.get<PaginatedResponse<PaymentRequest>>(`/payment-requests/task/${operationTaskId}?${params}`);
    return response.data;
  },

  getByStatus: async (status: PaymentRequestStatus, filters: { page?: number; size?: number } = {}) => {
    const { page = 0, size = 20 } = filters;
    const params = new URLSearchParams({ page: page.toString(), size: size.toString() });
    const response = await apiClient.get<PaginatedResponse<PaymentRequest>>(`/payment-requests/status/${status}?${params}`);
    return response.data;
  },

  getPending: async () => {
    const response = await apiClient.get<PaymentRequest[]>('/payment-requests/pending');
    return response.data;
  },

  createContractor: async (data: CreatePaymentRequestRequest) => {
    const response = await apiClient.post<PaymentRequest>('/payment-requests/contractor', data);
    return response.data;
  },

  createGeneral: async (data: GeneralPaymentRequestRequest) => {
    const response = await apiClient.post<PaymentRequest>('/payment-requests/general', data);
    return response.data;
  },

  createProcurement: async (data: CreateProcurementRequestRequest) => {
    const response = await apiClient.post<PaymentRequest>('/payment-requests/procurement', data);
    return response.data;
  },

  approve: async (id: string) => {
    const response = await apiClient.put<PaymentRequest>(`/payment-requests/${id}/approve`);
    return response.data;
  },

  reject: async (id: string, reason: string) => {
    const response = await apiClient.put<PaymentRequest>(
      `/payment-requests/${id}/reject?reason=${encodeURIComponent(reason)}`
    );
    return response.data;
  },

  countPending: async () => {
    const response = await apiClient.get<number>('/payment-requests/pending/count');
    return response.data;
  },

  search: async (params: { page?: number; size?: number; search?: string; status?: PaymentRequestStatus; projectId?: string; farmOperationId?: string; operationTaskId?: string } = {}) => {
    const { page = 0, size = 10, search, status, projectId, farmOperationId, operationTaskId } = params;
    const qs = new URLSearchParams({ page: page.toString(), size: size.toString() });
    if (search) qs.set('search', search);
    if (status) qs.set('status', status);
    if (projectId) qs.set('projectId', projectId);
    if (farmOperationId) qs.set('farmOperationId', farmOperationId);
    if (operationTaskId) qs.set('operationTaskId', operationTaskId);
    const response = await apiClient.get<PaginatedResponse<PaymentRequest>>(`/payment-requests/search?${qs}`);
    return response.data;
  },
};

export default paymentRequestService;
